import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import "../../styles/common/popup.scss";
import dragIcon from "../../assets/svg/dropIcon.svg";

const OverallMaterialPopup = ({
  open,
  handleClose,
  onSave,
  mode = "create",
  material,
}) => {
  const [type, setType] = useState("pdf");
  const [name, setName] = useState("");
  const [link, setLink] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState("");
  const [nameError, setNameError] = useState("");
  const [contentError, setContentError] = useState("");

  const typeOptions = [
    { value: "pdf", label: "PDF" },
    { value: "videos", label: "Video" },
    { value: "audio", label: "Audio" },
    { value: "image", label: "Images" },
    { value: "ppt", label: "PowerPoint" },
  ];

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "var(--color-bg)",
      borderColor: "var(--border-color)",
      color: "var(--color-text-dark)",
      minHeight: "40px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "var(--color-primary)",
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "var(--color-bg)",
      zIndex: 10001,
      position: "absolute",
      width: "100%",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 10001,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "var(--color-primary)"
        : state.isFocused
          ? "var(--color-hover)"
          : "var(--color-bg)",
      color: state.isSelected ? "white" : "var(--color-text-dark)",
      "&:hover": {
        backgroundColor: "var(--color-hover)",
      },
      fontSize: "var(--fs-md)",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "var(--color-text-dark)",
      fontSize: "var(--fs-md)",
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: "var(--fs-md)",
    }),
  };

  useEffect(() => {
    setFile(null);
    setFileError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [type]);

  const isValidFileType = (file, type) => {
    const mime = file.type;

    switch (type) {
      case "pdf":
        return mime === "application/pdf";
      case "ppt":
        return (
          mime === "application/vnd.ms-powerpoint" ||
          mime ===
            "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        );
      case "videos":
        return mime.startsWith("video/");
      case "audio":
        return mime.startsWith("audio/");
      case "image":
        return mime.startsWith("image/");
      default:
        return false;
    }
  };

  const isValidUrl = (value) => {
    if (!value || !value.trim()) return false;
    const trimmed = value.trim();
    const urlRegex =
      /^(https?:\/\/)([\w-]+\.)+[\w-]+(\/[\w\-._~:/?#\[\]@!$&'()*+,;=]*)?$/;
    return urlRegex.test(trimmed);
  };

  const getAcceptForType = (t) => {
    switch (t) {
      case "pdf":
        return "application/pdf";
      case "ppt":
        return ".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation";
      case "videos":
        return "video/*";
      case "audio":
        return "audio/*";
      case "image":
        return "image/*";
      default:
        return "";
    }
  };

  useEffect(() => {
    if (open) {
      if (mode === "edit" && material) {
        setType(material?.materials_type || "pdf");
        setName(material?.name || material?.title || "");
        setLink(material?.content || material?.link || "");
      } else {
        setType("pdf");
        setName("");
        setLink("");
      }
      setFile(null);
      setSubmitting(false);
      setNameError("");
      setContentError("");
    }
  }, [open, mode, material]);

  const handleMakeEmpty = () => {
    setName("");
    setLink("");
    setFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasError = false;

    if (!name || !name.trim()) {
      setNameError("Name is required");
      hasError = true;
    } else {
      setNameError("");
    }

    if (!link.trim() && !file) {
      setContentError("Please provide a link or upload a file");
      hasError = true;
    } else if (link.trim() && !isValidUrl(link)) {
      setContentError(
        "Please enter a valid URL (starting with http:// or https://)",
      );
      hasError = true;
    } else {
      setContentError("");
    }

    if (hasError) return;
    if (submitting) return;
    setSubmitting(true);

    const payload = {
      name,
      materials_type: type,
      content: link || "",
    };

    try {
      await onSave(payload, file);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];

      if (!isValidFileType(droppedFile, type)) {
        setFileError(`Invalid file type for ${type.toUpperCase()}`);
        return;
      }

      setFileError("");
      setFile(droppedFile);
      setLink("");
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      if (!isValidFileType(selectedFile, type)) {
        setFile(null);
        setFileError(`Invalid file type for ${type.toUpperCase()}`);
        e.target.value = "";
        return;
      }

      setFileError("");
      setFile(selectedFile);
      setLink("");
      setContentError("");
    }
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleLinkChange = (e) => {
    const newLink = e.target.value;
    setLink(newLink);
    if (newLink) {
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
    if (!newLink.trim()) {
      setContentError("");
    } else if (!isValidUrl(newLink)) {
      setContentError(
        "Please enter a valid URL (starting with http:// or https://)",
      );
    } else {
      setContentError("");
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  if (!open) return null;

  const isSaveDisabled = submitting || !name.trim() || (!link.trim() && !file);

  return createPortal(
    <div
      className={`common-popup-overlay ${open ? "" : "hidden"} high-z-index`}
      onClick={handleClose}
    >
      <div
        className="common-popup-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-header">
          <h2>
            {mode === "edit"
              ? "Edit Overall Material"
              : "Create Overall Material"}
          </h2>
          <button
            type="button"
            className="popup-close-button"
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <div className="popup-content">
          <form onSubmit={handleSubmit}>
            <div className="popup-form-group">
              <label htmlFor="overall-material-type">Type</label>
              <Select
                id="overall-material-type"
                className="popup-select-react"
                classNamePrefix="popup-select"
                value={
                  typeOptions.find((option) => option.value === type) || null
                }
                onChange={(selectedOption) => {
                  if (!selectedOption) return;
                  setType(selectedOption.value);
                  handleMakeEmpty();
                }}
                options={typeOptions}
                styles={selectStyles}
                isSearchable={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="popup-form-group">
              <label htmlFor="overall-material-name">Name</label>
              <input
                id="overall-material-name"
                type="text"
                className="popup-input"
                placeholder="Name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (e.target.value.trim()) {
                    setNameError("");
                  }
                }}
              />
              {nameError && <p className="file-error-text">{nameError}</p>}
            </div>

            <div className="popup-form-group">
              <label htmlFor="overall-material-link">Link</label>
              <input
                id="overall-material-link"
                type="text"
                className="popup-input"
                placeholder="URL / Link"
                value={link}
                onChange={handleLinkChange}
                disabled={!!file}
              />
              {contentError && !file && (
                <p className="file-error-text">{contentError}</p>
              )}
            </div>

            <div className="popup-form-group">
              <label>Upload File</label>
              <div
                className={`drop-area ${dragActive ? "drag-active" : ""}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={triggerFileInput}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  accept={getAcceptForType(type)}
                />

                {!file ? (
                  <div className="upload-ui">
                    <div className="upload-icon">
                      <img
                        src={dragIcon}
                        alt="Upload"
                        width={48}
                        height={48}
                        draggable={false}
                      />
                    </div>

                    <p className="upload-text">
                      Drag & drop files or <span>Browse</span>
                    </p>
                  </div>
                ) : (
                  <div className="upload-progress">
                    <div className="file-row">
                      <span className="file-name">{file.name}</span>

                      <button
                        type="button"
                        className="remove-file"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile();
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {fileError && <p className="file-error-text">{fileError}</p>}
            </div>

            <div className="popup-actions">
              <button
                type="submit"
                className="popup-submit-button"
                disabled={isSaveDisabled}
              >
                {mode === "edit" ? "Save Changes" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default OverallMaterialPopup;
